/**
 * WHAT IS DONE, WHAT IS MISSING, AND THE WAY BACK INTO THE SETUP.
 * ==============================================================
 *
 * THE FIELD FINDING OF 2026-10-20, in the Owner's words: *"לא מצאנו דרך להמשיך להזמנת מנהל הלקוח
 * דרך הממשק … בכרטיס אין פעולת המשך הקמה."*
 *
 * The resume screen existed and worked. It reads the customer's state from the server and lands on
 * the step that is actually next — `/platform/tenants/{uid}/provisioning`. What was missing was any
 * way to REACH it: `PlatformSubscriptionScreen` offered the link only inside its "this customer has
 * no subscription" branch, so opening a subscription removed the only door to the three steps that
 * follow it. A customer half-way through setup could be finished only by typing an address, and the
 * Owner is explicitly not to be asked to do that.
 *
 * So the door is this panel, it is shown wherever the customer is shown, and it names the state
 * rather than only offering a button — because *"הצג מה הושלם ומה חסר"* was the other half of the
 * instruction.
 *
 * ── EVERY STEP IS A SERVER FACT, NOT A GUESS ────────────────────────────────────────────────────
 *
 *   subscription   `subscriptionState === null` is the server saying there is no live subscription.
 *   modules        the subscription's own licensed modules. The provisioning screen uses exactly
 *                  this to decide between step 3 and step 4, so the two cannot disagree about what
 *                  "has modules" means. `null` where the caller has not read them (the tenant list
 *                  row does not carry them) — and then the step is reported as UNKNOWN rather than
 *                  as missing, because a panel that invents "חסר" sends an operator to redo work.
 *   administrator  `seatCount === 0`. An invitation consumes a seat the moment it is issued
 *                  (`MembershipSummary.consumesSeat`: *"`invited` and `active` consume a seat.
 *                  Stated by the server, never derived here"*), so a customer with no seats has
 *                  nobody — invited or signed in.
 *
 * It renders NOTHING when the three are complete. A finished customer needs no setup panel, and a
 * permanent "ההקמה הושלמה" block on every card is noise that teaches people to stop reading.
 */
import type { JSX } from 'react';

import type { SubscriptionState } from '../api/contract';
import { routes } from '../routes';

export interface SetupProgressPanelProps {
  readonly tenantUid: string;
  /** `null` ⇒ the server reports no live subscription. */
  readonly subscriptionState: SubscriptionState | null;
  /** The subscription's licensed modules, or `null` where this caller has not read them. */
  readonly licensedModules: readonly string[] | null;
  /** Seats consumed: an invited or active member each hold one. */
  readonly seatCount: number;
}

type StepState = 'done' | 'missing' | 'unknown';

interface Step {
  readonly labelHe: string;
  readonly state: StepState;
  readonly whatHe: string;
}

const MARK: Readonly<Record<StepState, string>> = {
  done: '✓',
  missing: '•',
  unknown: '?',
};

export function SetupProgressPanel({
  tenantUid,
  subscriptionState,
  licensedModules,
  seatCount,
}: SetupProgressPanelProps): JSX.Element | null {
  const steps: readonly Step[] = [
    {
      labelHe: 'מנוי',
      state: subscriptionState === null ? 'missing' : 'done',
      whatHe:
        subscriptionState === null
          ? 'ללקוח אין מנוי, ולכן אין לו מכסות ואין לו זכאות לשום מודול.'
          : 'נפתח מנוי ללקוח.',
    },
    {
      labelHe: 'זכאות למודולים',
      state:
        subscriptionState === null
          ? 'missing'
          : licensedModules === null
            ? 'unknown'
            : licensedModules.length === 0
              ? 'missing'
              : 'done',
      whatHe:
        subscriptionState === null
          ? 'אין מנוי שאפשר לתלות בו זכאות.'
          : licensedModules === null
            ? 'המודולים של המנוי לא נקראו במסך הזה. מסך המנוי מציג אותם.'
            : licensedModules.length === 0
              ? 'המנוי אינו מזכה באף מודול. זכאות שהחבילה כוללת מוחלת במסך המנוי; מוצר נוסף נמכר בנפרד.'
              : `המנוי מזכה ב־${String(licensedModules.length)} מודולים.`,
    },
    {
      labelHe: 'מנהל הלקוח',
      state: seatCount === 0 ? 'missing' : 'done',
      /*
        ── AND WHERE IT IS DONE. **2026-11-04.** ──────────────────────────────────────

        **The Owner:** *"ציין היכן בדיוק נמצאות הזמנת מנהל הלקוח ושליחה חוזרת."*

        This named the missing step and stopped. He looked for the two controls on this card and
        they are NOT ON IT -- which was the real answer, and nothing said so. Measured in the
        route table and the API: the FIRST admin is invited inside the provisioning flow, which
        the panel's own button below opens; a RESEND is `tenant.user.invitation.resend`, which
        lives on the OFFICE's users screen and not on any platform screen. Two different places,
        neither of them here, and a reader was left to conclude the card was broken.
      */
      whatHe:
        seatCount === 0
          ? 'לא הוזמן מנהל ראשון, ולכן אף אחד אינו יכול להיכנס לארגון הזה. ההזמנה נשלחת בתוך המשך ההקמה, בכפתור שלמטה — אין כפתור הזמנה נפרד בכרטיס הזה.'
          : 'קיים לפחות משתמש אחד — הזמנה או חשבון פעיל. שליחה חוזרת של הזמנה נעשית במסך המשתמשים של הארגון עצמו, לא מכאן.',
    },
  ];

  // A finished customer gets no panel at all. `unknown` is not completeness: it is a step this
  // caller cannot speak for, so the door stays visible.
  if (steps.every((step) => step.state === 'done')) return null;

  const missing = steps.filter((step) => step.state === 'missing');

  return (
    <section className="notice" data-tone="warn" aria-label="מצב ההקמה של הלקוח">
      <h2 className="notice__title">
        {missing.length === 0
          ? 'ההקמה של הלקוח — יש שלב שלא נקרא במסך הזה'
          : `ההקמה של הלקוח לא הושלמה — חסר: ${missing.map((step) => step.labelHe).join(', ')}`}
      </h2>
      <ul className="notice__body">
        {steps.map((step) => (
          <li key={step.labelHe}>
            <span dir="ltr">{MARK[step.state]}</span> <strong>{step.labelHe}</strong> — {step.whatHe}
          </li>
        ))}
      </ul>
      <p className="notice__body">
        ההמשך נפתח באותו תהליך ובאותה רשומה, בשלב שהשרת אומר שהוא הבא. אין צורך ליצור לקוח או מנוי
        נוסף, ואין צורך בעריכת הכתובת.
      </p>
      <div className="actions-row">
        <a className="btn btn--nav" href={routes.platformTenantProvisioningResume(tenantUid)}>
          המשך הקמת הלקוח
        </a>
      </div>
    </section>
  );
}
